<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core'

import axios from 'axios'

import { useMarketingFlowTemplates } from '@/composables/marketingFlowsTemplates'
import useLegacy from '@/views/autopilot/composables/legacy'
import { useMessageLengthCalculation } from '@/views/autopilot/useMessageLengthCalculation'
import { isTransactionalDefaultTrigger } from './utils'

import Error from '@/components/errors/Error.vue'

import ExitConfirmationModal from './Components/ExitConfirmationModal.vue'
import MarketingFlowActionSettings from './Components/MarketingFlowActionSettings.vue'
import MarketingFlowDelaySettings from './Components/MarketingFlowDelaySettings.vue'
import MarketingFlowPreview from './Components/MarketingFlowPreview.vue'
import MarketingFlowTopBar from './Components/MarketingFlowTopBar.vue'
import MarketingFlowTriggerSettings from './Components/MarketingFlowTriggerSettings.vue'
import ReplaceLegacyModal from './Components/ReplaceLegacyModal.vue'
import SaveConfirmationModal from './Components/SaveConfirmationModal.vue'

import type { Audience, LoyaltyReward } from '@/types/types'
import ActivationConfirmationModal from './Components/ActivationConfirmationModal.vue'
import DuplicationConfirmationModal from './Components/DuplicationConfirmationModal.vue'
import type { MarketingFlowStep } from './types/marketingFlow'
import { useContentScopeEntry } from '@/composables/contentScope'
import type { ContentScope } from '@/types/contentScope'

const { t } = useI18n()

const { fetchAllRewards } = useReward()
const { fetchMerchantAudiences } = useAudience()
const { stores, user } = useLocalStorage()
const notification = useNotification()
const { send } = useEvent()
const {
  fetchMarketingFlow,
  fetchMarketingFlowPerformance,
  generateNewVersionName,
  isActionStepCompleted,
  isDelayStepCompleted,
  isTriggerStepCompleted,
  isUsingTemplate,
  saveMarketingFlow
} = useMarketingFlow()
const { triggerSettings, delaySettings, actionSettings, marketingFlowName, currentStep, setTemplate, resetTemplate } =
  useMarketingFlowTemplates()
const { isLegacyCustomerBirthdayActive, isLegacyCustomerVipActive, isLegacyCustomerAtRiskActive } = useLegacy()
const {
  messageLength,
  variablesLength,
  isCheckingMessageLength,
  handleMessageLengthCalculation: calculateMessageLength,
  getVariablesLength
} = useMessageLengthCalculation()

const { error, hasError } = useError()

const router = useRouter()
const route = useRoute()

const isLargeScreen = useMediaQuery('(min-width: 1024px)')
const actionSettingsRef = ref<typeof MarketingFlowActionSettings | null>(null)

const isLoaded = ref<boolean>(false)
const isSaving = ref<boolean>(false)
const isChangesToSave = ref<boolean>(false)

const stepInError = ref<MarketingFlowStep[]>([])

const audiences = ref<Audience[]>(null)
const rewards = ref<LoyaltyReward[]>(null)

const marketingFlowId = ref<string>(null)
const { scope, isScopeModalOpen, confirmScope, askScope } = useContentScopeEntry()

const updateScope = (value: ContentScope | null | undefined) => {
  scope.value = value
  isChangesToSave.value = true
}

const segmentAudienceCount = ref<number | null>(null)
const isConfirming = ref(false)

const lastUpdatedAt = ref<Date>(null)

const isCriticalChangesToSave = ref<boolean>(false)
const isAlreadyActive = ref<boolean>(false)
const isStatsLoaded = ref<boolean>(false)
const hasAlreadyBeenTriggered = ref<boolean>(null)

const templateUsed = ref<string | null>(null)

const modalOpened = ref<'activation' | 'exit' | 'replaceLegacy' | 'duplication' | 'saveConfirmation' | null>(null)

const hasAnyError = computed(() => {
  return hasError.value
})

const isAlreadyActiveOrExecuted = computed(() => {
  return isAlreadyActive.value === true || (isStatsLoaded.value && hasAlreadyBeenTriggered.value === true)
})

const isDelayStepAvailable = computed(() => {
  return isTriggerStepCompleted(triggerSettings.value) || actionSettings.value.actions.length > 0
})

const isActionStepAvailable = computed(() => {
  return (
    (isTriggerStepCompleted(triggerSettings.value) &&
      isDelayStepCompleted(delaySettings.value, triggerSettings.value.triggerType)) ||
    actionSettings.value.actions.length > 0
  )
})

const isEndStepAvailable = computed(() => {
  return (
    isTriggerStepCompleted(triggerSettings.value) &&
    isDelayStepCompleted(delaySettings.value, triggerSettings.value.triggerType) &&
    isActionStepCompleted(actionSettings.value, triggerSettings.value.selectedStores)
  )
})

const isAllStepsCompleted = computed(() => {
  return (
    isTriggerStepCompleted(triggerSettings.value) &&
    isDelayStepCompleted(delaySettings.value, triggerSettings.value.triggerType) &&
    isActionStepCompleted(actionSettings.value, triggerSettings.value.selectedStores)
  )
})

const goToTriggerStep = () => {
  currentStep.value = 'trigger'
}
const goToDelayStep = () => {
  if (isDelayStepAvailable.value) {
    currentStep.value = 'delay'
  }
}
const goToActionStep = () => {
  if (isActionStepAvailable.value) {
    currentStep.value = 'action'
  }
}
const goToEndStep = () => {
  if (isEndStepAvailable.value) {
    currentStep.value = 'end'
  }
}

const returnToList = () => {
  if (marketingFlowId.value) {
    router.push(`/marketing-flow/detail/${marketingFlowId.value}`)
  } else {
    router.push('/autopilots')
  }
}

const handleOnChanges = (change?: { isCritical: boolean; field?: string; value?: any } | string) => {
  if (!change) return

  const isCritical = typeof change === 'object' ? change.isCritical : false
  const field = typeof change === 'object' ? change.field : 'name'
  const value = typeof change === 'object' ? change.value : change

  isChangesToSave.value = true
  isCriticalChangesToSave.value = isCriticalChangesToSave.value || isCritical

  if (field === 'triggerType') {
    actionSettings.value.isTransactional = isTransactionalDefaultTrigger(value)
    calculateMessageLength(actionSettings.value, availableStores.value)
  }

  if (isUsingTemplate.value && route.query.fromTemplate) {
    send('segment', 'automation.template.edited', {
      template_name: route.query.fromTemplate,
      field: field,
      value: value
    })
  }
}
const handleStoreUpdate = () => {
  calculateMessageLength(actionSettings.value, availableStores.value)
}

const handleCloseModal = () => {
  modalOpened.value = null
}

const getBackendErrorMessage = (error: unknown) => {
  const apiError = error as { response?: { data?: { message?: string; errors?: Record<string, string[] | string> } } }
  const validationErrors = apiError?.response?.data?.errors

  if (validationErrors && typeof validationErrors === 'object') {
    const firstError = Object.values(validationErrors)
      .flatMap((errorValue) => (Array.isArray(errorValue) ? errorValue : [errorValue]))
      .find(Boolean)

    if (firstError) {
      return String(firstError)
    }
  }

  return apiError?.response?.data?.message || t('pages.marketingFlow.marketingFlowEditPage.unexpectedError')
}

const skippedActivation = () => {
  if (route.query.fromTemplate) {
    send('segment', 'automation.template.skipped_activation', {
      template_name: route.query.fromTemplate,
      step: currentStep.value
    })
  }
}
const handleSaveAndQuitMarketingFlow = async (skipDuplicate?: boolean) => {
  if (isAlreadyActiveOrExecuted.value && isCriticalChangesToSave.value && !skipDuplicate) {
    modalOpened.value = 'duplication'
    return
  }

  try {
    skippedActivation()

    await saveCurrentMarketingFlow()

    handleCloseModal()

    notification.create('success', {
      text: t('pages.marketingFlow.marketingFlowEditPage.scenarioSavedSuccessfully'),
      timeout: 3000
    })

    returnToList()
  } catch (error) {
    if (error?.message === 'processing import or segment') {
      notification.create('error', {
        text: t('pages.marketingFlow.marketingFlowEditPage.processingSegementOrImportError'),
        timeout: 3000
      })
      handleCloseModal()
    } else {
      notification.create('error', {
        text: getBackendErrorMessage(error),
        timeout: 3000
      })
    }
  }
}

const handleSaveConfirmation = () => {
  if (isAlreadyActiveOrExecuted.value && isCriticalChangesToSave.value) {
    modalOpened.value = 'duplication'
    return
  }

  if (!isAlreadyActive.value) {
    modalOpened.value = 'saveConfirmation'
    return
  }

  handleSaveAndQuitMarketingFlow(true)
}

const handleActivationConfirmation = async () => {
  if (isReplacingLegacy.value) {
    modalOpened.value = 'replaceLegacy'
    return
  }

  if (shouldShowAudienceOptions.value) {
    segmentAudienceCount.value = await fetchAudienceCount()

    if (segmentAudienceCount.value === 0) {
      await activateMarketingFlow(false)
      return
    }

    modalOpened.value = 'activation'
    return
  }

  await activateMarketingFlow()
}

const handleActivateMarketingFlow = async (executeOnExistingMembers?: boolean) => {
  if (isConfirming.value) return
  isConfirming.value = true

  try {
    await activateMarketingFlow(executeOnExistingMembers)
  } finally {
    isConfirming.value = false
  }
}

const isReplacingLegacy = computed(() => {
  if (!templateUsed.value && !route.query.fromTemplate) {
    return false
  }

  const template = templateUsed.value ?? (route.query.fromTemplate as string)
  if (template === 'CustomerBirthday') {
    return isLegacyCustomerBirthdayActive.value
  }

  if (template === 'CustomerVip') {
    return isLegacyCustomerVipActive.value
  }

  if (template === 'CustomerAtRisk') {
    return isLegacyCustomerAtRiskActive.value
  }

  return false
})

const fetchAudienceCount = async (): Promise<number | null> => {
  if (!triggerSettings.value?.selectedAudience || !triggerSettings.value?.selectedStores?.length) {
    return null
  }

  try {
    const { data } = await axios.get(`/merchants/${user.value.merchant.id}/segments/statistics`, {
      params: {
        storeIds: triggerSettings.value.selectedStores,
        segmentIds: [triggerSettings.value.selectedAudience]
      }
    })

    return data.data.length > 0 ? data.data[0]?.customer_count : 0
  } catch (error) {
    console.error('Error fetching audience count:', error)
    return null
  }
}

const shouldShowAudienceOptions = computed(
  () => triggerSettings.value?.triggerType === 'SEGMENT_ENTRY' && triggerSettings.value?.selectedAudience
)

const fetchSegments = async () => {
  const data = await fetchMerchantAudiences({
    pagination: { pageSize: 1000, page: 1 }
  })

  audiences.value = data.data
}

const fetchRewards = async () => {
  const data = await fetchAllRewards()
  rewards.value = data
}

const exitPage = async () => {
  if (isUsingTemplate.value && isChangesToSave.value === false && route.query.fromTemplate) {
    send('segment', 'automation.template.abandoned', {
      template_name: route.query.fromTemplate,
      step: currentStep.value
    })
  }

  if (isChangesToSave.value && triggerSettings.value.triggerType) {
    modalOpened.value = 'exit'
  } else {
    returnToList()
  }
}

const replaceLegacyMarketingFlow = async () => {
  handleCloseModal()
  activateMarketingFlow()

  send('segment', 'automation.template.replace_legacy.confirmed', {
    template_name: route.query.fromTemplate
  })
}

const replaceLegacyMarketingFlowCancel = () => {
  handleCloseModal()

  send('segment', 'automation.template.replace_legacy.canceled', {
    template_name: route.query.fromTemplate
  })
}

const activateMarketingFlow = async (executeOnExistingMembers?: boolean) => {
  try {
    await saveCurrentMarketingFlow({ isActive: true, executeOnExistingMembers })

    if (isUsingTemplate.value && route.query.fromTemplate) {
      send('segment', 'automation.template.activated', {
        template_name: route.query.fromTemplate
      })
    }

    handleCloseModal()

    notification.create('success', {
      text: t('pages.marketingFlow.marketingFlowEditPage.scenarioActivatedSuccessfully'),
      timeout: 3000
    })

    returnToList()
  } catch (error) {
    if (error?.message === 'processing import or segment') {
      notification.create('error', {
        text: t('pages.marketingFlow.marketingFlowEditPage.processingSegementOrImportError'),
        timeout: 3000
      })
      handleCloseModal()
    } else {
      notification.create('error', {
        text: getBackendErrorMessage(error),
        timeout: 3000
      })
    }
  }
}

const getMarketingFlow = async () => {
  const {
    lastUpdatedAtValue,
    marketingFlowNameValue,
    triggerSettingsValue,
    delaySettingsValue,
    actionSettingsValue,
    isActiveValue,
    templateUsedValue,
    scopeValue
  } = await fetchMarketingFlow({
    marketingFlowId: Number(marketingFlowId.value),
    audiences: audiences.value,
    rewards: rewards.value as LoyaltyReward[]
  })

  lastUpdatedAt.value = lastUpdatedAtValue
  marketingFlowName.value = marketingFlowNameValue
  triggerSettings.value = triggerSettingsValue
  delaySettings.value = delaySettingsValue
  actionSettings.value = actionSettingsValue
  isAlreadyActive.value = isActiveValue

  templateUsed.value = templateUsedValue
  scope.value = scopeValue
}

const saveCurrentMarketingFlow = async (options?: { isActive?: boolean; executeOnExistingMembers?: boolean }) => {
  const { isActive, executeOnExistingMembers } = options ?? {}

  const { lastUpdatedAtValue, marketingFlowIdValue } = await saveMarketingFlow({
    marketingFlowName: marketingFlowName.value,
    merchantId: user.value.merchant.id,
    isActive: isActive ?? isAlreadyActive.value,
    actionSettings: actionSettings.value,
    delaySettings: delaySettings.value,
    triggerSettings: triggerSettings.value,
    marketingFlowId: marketingFlowId.value ? Number(marketingFlowId.value) : null,
    executeOnExistingMembers: executeOnExistingMembers ?? false,
    forceEdit: true,
    scope: scope.value ?? undefined
  })

  lastUpdatedAt.value = lastUpdatedAtValue
  marketingFlowId.value = marketingFlowIdValue
}

const duplicateMarketingFlow = async () => {
  try {
    const { marketingFlowIdValue } = await saveMarketingFlow({
      marketingFlowName: generateNewVersionName(marketingFlowName.value),
      merchantId: user.value.merchant.id,
      isActive: false,
      actionSettings: actionSettings.value,
      delaySettings: delaySettings.value,
      triggerSettings: triggerSettings.value
    })

    handleCloseModal()

    notification.create('success', {
      text: t('pages.marketingFlow.marketingFlowEditPage.scenarioDuplicatedSuccessfully'),
      timeout: 3000
    })

    router.push(`/marketing-flow/edit/${marketingFlowIdValue}`)
  } catch (error) {
    notification.create('error', {
      text: getBackendErrorMessage(error),
      timeout: 3000
    })
  }
}

watch(
  () => triggerSettings.value.triggerType,
  (newType) => {
    if (newType === 'BIRTHDAY') {
      delaySettings.value = {
        ...delaySettings.value,
        daysBeforeAction: 0,
        sendImmediatlyAfterTrigger: false,
        sendAfterDelay: true,
        sendAtSpecificHour: true,
        delayUnit: 'DAYS'
      }
    }

    if (newType !== 'SEGMENT_ENTRY' && newType !== 'SEGMENT_EXIT') {
      triggerSettings.value.selectedAudience = null
    }
  },
  { immediate: true }
)

const defineTemplate = async () => {
  try {
    const audienceId = route.query.audienceId ? Number(route.query.audienceId) : undefined
    await setTemplate(route.query.fromTemplate as string, audienceId)

    send('segment', 'automation.template.created', {
      template_name: route.query.fromTemplate
    })
  } catch (error) {
    notification.create('error', {
      text: getBackendErrorMessage(error),
      timeout: 3000
    })
  }
}

const availableStores = computed(() => {
  return stores.value?.filter((store) => triggerSettings.value?.selectedStores?.includes(store?.id)) ?? []
})

onMounted(async () => {
  try {
    await Promise.all([fetchSegments(), fetchRewards()])

    if (route.params.marketingFlowId) {
      marketingFlowId.value = route.params.marketingFlowId as string
    }

    if (marketingFlowId.value) {
      await getMarketingFlow()

      const { triggeredCustomers } = await fetchMarketingFlowPerformance({
        marketingFlowId: Number(marketingFlowId.value)
      })

      hasAlreadyBeenTriggered.value = triggeredCustomers > 0
      isStatsLoaded.value = true
    } else if (route.query.fromTemplate) {
      await defineTemplate()
    }

    if (!marketingFlowId.value && isTransactionalDefaultTrigger(triggerSettings.value.triggerType)) {
      actionSettings.value.isTransactional = true
    }

    if (route.query?.step) {
      currentStep.value = route.query.step as MarketingFlowStep
    }

    // Initialize message length calculation
    const messageAction = actionSettings.value?.actions?.find((action) =>
      ['SEND_SMS', 'SEND_PUSH'].includes(action.actionType)
    )
    if (messageAction) {
      await getVariablesLength(actionSettings.value, availableStores.value)
      calculateMessageLength(actionSettings.value, availableStores.value)
    }
  } finally {
    isLoaded.value = true
    if (!marketingFlowId.value) askScope()
  }
})

watch(messageLength, (newLength) => {
  actionSettings.value.computedMessageLength = newLength
})

onUnmounted(() => {
  resetTemplate()
})
</script>

<template>
  <div
    v-if="hasAnyError"
    class="dotted-background flex h-screen w-full flex-col items-center justify-center bg-content-bg"
  >
    <Error :error="error" />
  </div>
  <template v-else>
    <TheTooSmallScreenPage v-if="!isLargeScreen" @on-go-back="returnToList" />

    <div
      v-if="!isLoaded"
      class="dotted-background flex h-screen w-full flex-col items-center justify-center bg-content-bg"
    >
      <div class="animate-[fade-in_1s_ease-in-out_0.5s_forwards] opacity-0">
        <Spinner class="mb-4 size-20 opacity-50" />
        <p class="text-foreground">{{ t('global.words.loading') }}...</p>
      </div>
    </div>

    <div v-if="isLoaded && isLargeScreen" class="h-screen bg-content-bg text-foreground">
      <div class="dotted-background flex h-full flex-col">
        <MarketingFlowTopBar
          v-model="marketingFlowName"
          :current-step="currentStep"
          :disabled="
            !isActionStepCompleted(actionSettings, triggerSettings.selectedStores) ||
            !isDelayStepCompleted(delaySettings, triggerSettings.triggerType) ||
            !isTriggerStepCompleted(triggerSettings) ||
            isCheckingMessageLength
          "
          :is-create-mode="!marketingFlowId"
          :is-saving="isSaving"
          :is-changes="isChangesToSave"
          :is-checking-message-length="isCheckingMessageLength"
          :last-updated-at="lastUpdatedAt"
          :is-already-active="isAlreadyActive"
          :trigger-settings="triggerSettings"
          :delay-settings="delaySettings"
          :action-settings="actionSettings"
          :message-length="messageLength"
          @on-save="handleSaveConfirmation"
          @on-exit="exitPage"
          @update:model-value="handleOnChanges"
        >
          <template #after-name>
            <ContentScopeDetailBadge
              :scope
              subject="automation"
              :editable="!marketingFlowId"
              @update:scope="updateScope"
            />
          </template>
        </MarketingFlowTopBar>
        <div class="flex w-full flex-1 flex-row" style="height: calc(100% - 78px)">
          <div
            class="flex max-h-full max-w-[50%] flex-col items-center border-r bg-white transition-width duration-300"
            :class="currentStep === 'end' ? 'w-0' : 'w-[600px] min-w-[530px]'"
          >
            <div v-show="currentStep === 'trigger'" class="h-full w-full">
              <MarketingFlowTriggerSettings
                v-model="triggerSettings"
                :is-active="isAlreadyActive"
                :audiences="audiences"
                :stores="stores"
                :rewards="rewards"
                @go-previous-step="returnToList"
                @go-next-step="goToDelayStep()"
                @on-changes="handleOnChanges"
                @fetch-rewards="fetchRewards"
                @update-stores="handleStoreUpdate"
              />
            </div>

            <div v-show="currentStep === 'delay'" class="h-full w-full">
              <MarketingFlowDelaySettings
                v-model="delaySettings"
                :trigger-type="triggerSettings.triggerType"
                @go-previous-step="goToTriggerStep()"
                @go-next-step="goToActionStep()"
                @on-changes="handleOnChanges"
              />
            </div>

            <div v-show="currentStep === 'action'" class="h-full w-full">
              <MarketingFlowActionSettings
                ref="actionSettingsRef"
                v-model="actionSettings"
                v-model:is-checking-message-length="isCheckingMessageLength"
                :stores="availableStores"
                :rewards="rewards"
                :audiences="audiences"
                :message-length="messageLength"
                :variables-length="variablesLength"
                :scenario-name="marketingFlowName"
                :trigger-type="triggerSettings.triggerType"
                @go-previous-step="goToDelayStep()"
                @go-next-step="goToEndStep()"
                @on-changes="handleOnChanges"
                @fetch-rewards="fetchRewards"
                @calculate-message-length="calculateMessageLength(actionSettings, availableStores)"
              />
            </div>
          </div>

          <div class="flex max-h-full flex-1 flex-col items-center overflow-auto px-8 py-16">
            <MarketingFlowPreview
              :trigger-settings="triggerSettings"
              :delay-settings="delaySettings"
              :action-settings="actionSettings"
              :audiences="audiences"
              :stores="stores"
              :rewards="rewards"
              :disabled-card-click="{
                trigger: false,
                delay: !isDelayStepAvailable,
                action: !isActionStepAvailable
              }"
              :step-has-error="{
                trigger: stepInError.includes('trigger'),
                delay: stepInError.includes('delay'),
                action: stepInError.includes('action')
              }"
              :show-stats="false"
              :current-step="currentStep"
              @trigger-card-clicked="goToTriggerStep()"
              @delay-card-clicked="goToDelayStep()"
              @action-card-clicked="goToActionStep()"
            />
          </div>
        </div>
      </div>
    </div>

    <SaveConfirmationModal
      :is-open="modalOpened === 'saveConfirmation'"
      :handle-close="handleCloseModal"
      @on-save="handleSaveAndQuitMarketingFlow"
      @on-activate="handleActivationConfirmation"
    />

    <ActivationConfirmationModal
      :is-open="modalOpened === 'activation'"
      :handle-close="handleCloseModal"
      :trigger-settings="triggerSettings"
      :audiences="audiences"
      :audience-count="segmentAudienceCount"
      :is-processing="isConfirming"
      @on-activate="handleActivateMarketingFlow"
      @on-save-and-quit="handleSaveAndQuitMarketingFlow"
    />

    <ExitConfirmationModal
      :is-open="modalOpened === 'exit'"
      :handle-close="handleCloseModal"
      :is-complete="isAllStepsCompleted && !isCheckingMessageLength"
      @on-exit="returnToList"
      @on-save-and-quit="handleSaveAndQuitMarketingFlow"
    />

    <DuplicationConfirmationModal
      :is-open="modalOpened === 'duplication'"
      :handle-close="handleCloseModal"
      @on-duplicate="duplicateMarketingFlow"
      @on-save-and-quit="() => handleSaveAndQuitMarketingFlow(true)"
    />

    <ReplaceLegacyModal
      :is-open="modalOpened === 'replaceLegacy'"
      :handle-close="handleCloseModal"
      @on-confirm="replaceLegacyMarketingFlow"
      @on-cancel="replaceLegacyMarketingFlowCancel"
    />
    <ContentScopeModal
      v-model:open="isScopeModalOpen"
      subject="automation"
      @confirm="confirmScope"
      @cancel="returnToList"
    />
  </template>
</template>

<style>
@keyframes fade-in {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
